export type PresenceStatusType =
  | 'online'
  | 'idle'
  | 'offline'
  | 'absent'
  | 'invited'
  | 'auto-logout'
  | 'none';

const NAMED_STATUSES = new Set(['online', 'idle', 'auto-logout', 'not-using']);

export function parsePresenceTimeMs(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  if (NAMED_STATUSES.has(value)) {
    return null;
  }
  const withUtc = /Z$|[+-]\d{2}:?\d{2}$/.test(value) || value.includes('T')
    ? value
    : `${value} UTC`;
  const ms = Date.parse(withUtc);
  return Number.isFinite(ms) ? ms : null;
}

/** Last connection / presence — not screenshot capture time. */
export function getLastSeenMs(user: {
  lastOnlineAtTz?: unknown;
  lastOnlineAt?: unknown;
  status?: unknown;
} | null | undefined): number | null {
  if (!user) {
    return null;
  }
  return (
    parsePresenceTimeMs(user.lastOnlineAtTz) ??
    parsePresenceTimeMs(user.lastOnlineAt) ??
    parsePresenceTimeMs(user.status)
  );
}

export function formatCompactDuration(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) {
    return 'a moment';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? '1 min' : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  const days = Math.floor(hours / 24);
  if (days === 1) {
    return '1 day';
  }
  if (days < 7) {
    return `${days} days`;
  }
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? '1 week' : `${weeks} weeks`;
}

export function formatLastSeenRelative(atMs: number, nowMs = Date.now()): string {
  const at = new Date(atMs);
  const now = new Date(nowMs);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOf(now) - startOf(at)) / 86400000);
  if (dayDiff === 1) {
    return 'yesterday';
  }
  if (dayDiff > 1) {
    if (dayDiff < 7) {
      return `${dayDiff} days ago`;
    }
    const weeks = Math.floor(dayDiff / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  const elapsed = Math.max(0, nowMs - atMs);
  if (elapsed < 60000) {
    return 'just now';
  }
  return `${formatCompactDuration(elapsed)} ago`;
}

export function formatExactPresenceTime(atMs: number): string {
  return new Date(atMs).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function getPresenceCaption(params: {
  statusType: PresenceStatusType | string;
  lastSeenMs: number | null;
  idleStartMs?: number | null;
  nowMs?: number;
}): string {
  const now = params.nowMs ?? Date.now();
  if (params.statusType === 'online' || params.statusType === 'none') {
    return '';
  }
  if (params.statusType === 'invited') {
    return 'Not joined yet';
  }
  if (params.statusType === 'idle') {
    if (params.idleStartMs == null) {
      return '';
    }
    return `Idle for ${formatCompactDuration(now - params.idleStartMs)}`;
  }
  if (params.lastSeenMs == null) {
    return 'No activity yet';
  }
  return `Last seen ${formatLastSeenRelative(params.lastSeenMs, now)}`;
}

export function getPresenceTooltip(params: {
  statusType: PresenceStatusType | string;
  lastSeenMs: number | null;
  idleStartMs?: number | null;
}): string {
  if (params.statusType === 'idle' && params.idleStartMs != null) {
    return `Idle since ${formatExactPresenceTime(params.idleStartMs)}`;
  }
  if (params.lastSeenMs == null) {
    return '';
  }
  if (params.statusType === 'online') {
    return '';
  }
  return `Last seen ${formatExactPresenceTime(params.lastSeenMs)}`;
}

export function formatScreenshotCell(relative: string): string {
  if (!relative || relative === '--') {
    return 'No screenshot yet';
  }
  return relative.replace(/^captured\s+/i, '');
}

export function compareLastSeenOldestFirst(aMs: number | null, bMs: number | null): number {
  const a = aMs == null ? Number.POSITIVE_INFINITY : aMs;
  const b = bMs == null ? Number.POSITIVE_INFINITY : bMs;
  return a - b;
}
